import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAppSelector } from '@/redux/store.hook';
import { getAuthSelector } from '@/redux/auth/auth.slice';

import { LoadingPage, Box } from '@/layout';
import TypeInformation from './TypeInformation';
import Participants from './Participants';
import DocTemplate from './DocTemplate';

import { getDealDetail } from '@/api/deal.api';
import { getUserProgress } from '@/api/auth.api';
import PATH from '@/router/path';
import PreviewInformation from './PreviewInformation';
import { dealType } from '@/data/select';
import { Icons, Notice } from './styles';
import { AccountVerifiedBadge, ECertBadge, IdentificationCheckBadge, SupplementalInfoBadge, BRBadge } from '@/components/UI/Badge';
import { Button } from '@/components/UI/Button';
import useResize from '@/hooks/useResize';
import { H4 } from '@/components/UI/Typography/Heading';

const FormStepper = () => {
  const { isTablet, isMobile } = useResize();
  const location = useLocation();
  const navigate = useNavigate();
  const { userData } = useAppSelector(getAuthSelector);
  const params = new URLSearchParams(location.search);
  const queryStep = params.get('step');
  const queryId = params.get('id');

  // @ts-ignore
  const { t } = useTranslation();
  const methods = useForm<DealFormFields>({
    defaultValues: {},
  });

  const [activeStep, setActiveStep] = useState(queryStep ? Number(queryStep) : 1);
  const [newDealId, setNewDealId] = useState<string>();
  const [dealDetail, setDealDetail] = useState<DealDetail>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [progressStatus, setProgressStatus] = useState<BadgeGroup>({
    company: '0',
    supplemental: '0',
    person: '0',
    eCert: '0',
    creditReport: '0',
    identification: '0',
    accountVerified: '0',
  });
  const [showProgressController, setShowProgressController] = useState(false);


  const [formData, setFormData] = useState<DealStep1FormRequest>({
    deal_id: undefined,
    deal_role_id: '',
    deal_type_id: '',
    name: '',
    description: '',
    expiry_date: '',
    category: '',
    languages: '',
    currency: '',
    category_other: '',
    languages_other: '',
    currency_other: '',
  });

  // 檢查若formData的name為''且step在第二步, 則step返回第一步
  useEffect(() => {
    if (activeStep === 2 && formData.name === '') {
      setActiveStep(1);
    }
  }, [activeStep]);

  // 每次步驟切換進行回到最頂層
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeStep]);

  const steps = t('pages.dealInitiation.steps', { returnObjects: true });

  const incrementQueryValue = (name: string, value: number) => {
    const searchParams = new URLSearchParams(location.search);
    const queryValue = parseInt(searchParams.get(name) || '0', 10);
    searchParams.set(name, (queryValue + value).toString());
    navigate(`${location.pathname}?${searchParams.toString()}`);
  }

  const handleNextStep = async () => {
    incrementQueryValue('step', 1);
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
    getDetail();
  };

  const handleBackStep = () => {
    incrementQueryValue('step', -1);
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
    getDetail();
  };

  const completedSteps = useCallback(() => Object.keys(steps).length, [steps]);

  const getStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <TypeInformation
            handleNextStep={handleNextStep}
            dealDetail={dealDetail}
            setIsLoading={setIsLoading}
            setFormData={setFormData}
            formData={formData}
          />
        );
      case 1:
        return (
          <PreviewInformation
            setNewDealId={setNewDealId}
            setIsLoading={setIsLoading}
            formData={formData}
            handleNextStep={handleNextStep}
            handleBackStep={handleBackStep}
          />
        );
      case 2:
        return <Participants handleNextStep={handleNextStep} handleBackStep={handleBackStep} queryId={queryId} />;
      case 3:
        navigate(`/deal`);
    }
  };

  const getDetail = useCallback(async () => {
    if (!queryId) return;
    try {
      const res = (await getDealDetail(queryId)).data;
      setDealDetail(res.data);
    } catch (error) {
      navigate(PATH.deal.index);
    }
  }, [queryId]);

  useEffect(() => {
    if (!newDealId) return;
    const query: { [key: string]: string } = {
      id: newDealId,
    };
    const searchParams = new URLSearchParams(location.search);
    for (const [key, value] of Object.entries(query)) {
      searchParams.set(key, value);
    }
    navigate(`${location.pathname}?${searchParams.toString()}`);
  }, [newDealId]);

  const checkBadgesProgress = useCallback(async () => {
    try {
      const res = (await getUserProgress()).data;
      if (res.success) {
        setProgressStatus(res.data);
        // 驗證若accountVerified, eCert, identification, supplemental其中一個為false則顯示提醒
        const { accountVerified, eCert, identification, supplemental } = res.data;
        const hasIncompleteBadge = !accountVerified || !eCert || !identification || !supplemental;

        setShowProgressController(hasIncompleteBadge);
      }
    } catch (error) {
      console.error(error);
    }
  }
    , []);

  useEffect(() => {
    // 驗證使用者的徽章
    if (userData) {
      checkBadgesProgress();
    }
    if (!queryId) return;
    getDetail();
  }, [getDetail, queryId]);

  // 確認是否顯示徽章
  const checkShowVerifiedBadge = (data: MemberProfile) => {
    // 有子母公司的情況需子公司與母公司皆有驗證才將驗證badge顯示
    if (data.verification.accountVerified && data.childCompanies) {
      const childVerified = data.childCompanies.every((item) => item.verification.accountVerified);
      if (childVerified) {
        return true;
      }
      return false;
    }

    // 梅子公司的情況只需母公司有驗證就將驗證badge顯示
    if (data.verification.accountVerified) {
      return true;
    }
    return false;
  };


  return (
    <>
      {
        showProgressController && (
          <Notice isTablet={isTablet} isMobile={isMobile}>
            <Icons>
              {!progressStatus.identification && <IdentificationCheckBadge width="24px" align="center" />}
              {!progressStatus.supplemental && <SupplementalInfoBadge width="24px" align="center" />}
              {!progressStatus.eCert && <ECertBadge width="24px" align="center" />}
              {!checkShowVerifiedBadge(userData) ? <AccountVerifiedBadge width="24px" align="center" /> : <></>}
            </Icons>
            <H4 className="text">{t('pages.dealInitiation.formStepper.step2.nobadges')}</H4>
            <Link to="/profile/edit">
              <Button>{t('buttons.go')}</Button>
            </Link>
          </Notice>
        )
      }
      <Box display={'flex'}>
        <LoadingPage isLoading={isLoading} />
        {/* Stepper 是從 0 開始 所以 activeStep 必須 -1 */}
        <Stepper activeStep={activeStep - 1} orientation="vertical" sx={{ paddingRight: '70px', maxHeight: '213px', width: '275px', flexShrink: '0' }}>
          {steps.map((s) => (
            <Step key={s.label}>
              <StepLabel>{s.label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        <FormProvider {...methods}>
          <Box width={'100%'}>{getStepContent(activeStep - 1)}</Box>
        </FormProvider>
      </Box>
    </>
  );
};

export default FormStepper;
